import { useCallback, useEffect, useMemo, useState } from "react";
import { ConfirmationDialog } from "../../../ui/components/ConfirmationDialog";
import { StatusReasonForm } from "../StatusReasonForm";
import { useChangeStatusWithReason } from "../../hooks/useChangeStatusWithReason";
import { mainTheme } from "../../../../theme/mainTheme";
import { RETURN_TO_REVISION, VerificationStatus } from "../../utils/getStatus";
import { useVerificationStatusChange } from "../../hooks/useVerificationStatusChange";
import { ICompanyVerificationStatusesConfig } from "../../network";
import { IModalButtons } from "../../hooks/useVerificationStatusActions";
import { useChangeRiskOrStatusWithRisk } from "../../hooks/useChangeRiskOrStatusWithRisk";
import { RiskLevelForm } from "../RiskLevelForm";

export type Props = {
  onClose: (reason: "cancel" | VerificationStatus) => void;
  open: boolean;
  isNamesEqual: boolean;
  verificationId: string;
  status: string;
  value: string;
  statusesConfig: ICompanyVerificationStatusesConfig;
  nextStatus: VerificationStatus;
  identificationDocumentType: "Passport" | "ID" | null;
  actions: IModalButtons;
  riskLevel: { riskLevel: string; riskLevelReason: string } | null;
};

export const DocumentReviewDialog = (props: Props) => {
  const {
    verificationId,
    onClose,
    open,
    value,
    status,
    isNamesEqual,
    identificationDocumentType,
    nextStatus,
    statusesConfig,
    actions,
    riskLevel,
  } = props;
  const [
    changeStatusResult,
    changeStatusLoading,
    changeStatusError,
    changeStatusRequest,
    setError,
  ] = useVerificationStatusChange(verificationId);

  const isAceptOrFinal = [
    VerificationStatus.ACCEPTED,
    VerificationStatus.ON_FINAL_REVIEW,
  ].includes(nextStatus);

  const [mainEntity, setMainEntity] = useState("");
  const [additionalEntity, setAdditionalEntity] = useState("");
  const [handleChangeStatusNegative, validationErrors, setValidationErrors] =
    useChangeStatusWithReason(
      value,
      mainEntity,
      additionalEntity,
      changeStatusRequest
    );
  const [handleChangeStatusPositive, changeRiskLoading, changeRiskError] =
    useChangeRiskOrStatusWithRisk(
      verificationId,
      mainEntity,
      additionalEntity,
      value,
      changeStatusRequest,
      riskLevel
    );

  useEffect(() => {
    if (isAceptOrFinal && riskLevel) {
      setMainEntity(riskLevel.riskLevel);
      setAdditionalEntity(riskLevel.riskLevelReason);
    }
  }, [isAceptOrFinal, riskLevel]);

  useEffect(() => {
    if (changeStatusResult) {
      onClose(changeStatusResult.status);
    }
  }, [changeStatusResult, onClose]);

  const handleCancel = useCallback(() => {
    setValidationErrors({});
    setError(null);
    onClose("cancel");
  }, [onClose, setValidationErrors, setError]);

  const handleOk = useCallback(() => {
    isAceptOrFinal
      ? handleChangeStatusPositive()
      : handleChangeStatusNegative();
  }, [handleChangeStatusNegative, handleChangeStatusPositive, isAceptOrFinal]);

  const { description, titleColor, descriptionColor } = useMemo(() => {
    let description = (
      <span>
        After this action, the entry will be
        {
          <span style={{ color: statusesConfig.object[nextStatus]?.color }}>
            {" "}
            {statusesConfig.object[nextStatus]?.displayName}
          </span>
        }
      </span>
    );

    let titleColor = actions[nextStatus]?.color;

    let descriptionColor = "inherit";

    if (isAceptOrFinal) {
      if (status === "valid") {
        description = isNamesEqual ? (
          description
        ) : identificationDocumentType ? (
          <span>
            Names from {identificationDocumentType} don't match with names from
            User Information! Are you sure you want to accept?
          </span>
        ) : (
          <span>"Binary date missing in identification documents"</span>
        );
        titleColor = isNamesEqual ? titleColor : mainTheme.palette.error.main;
        descriptionColor = isNamesEqual
          ? descriptionColor
          : mainTheme.palette.error.main;
      } else {
        titleColor = mainTheme.palette.error.main;
        descriptionColor = mainTheme.palette.error.main;
        description = (
          <span>
            The database screening is not in "Valid" status - some KYC steps
            might be missing. Are you sure you want to accept?
          </span>
        );
      }
    } else if (value === RETURN_TO_REVISION) {
      description = (
        <span>
          After this action, the entry will be returned to the previous step in
          the review process
        </span>
      );
    }
    return { description, titleColor, descriptionColor };
  }, [
    actions,
    identificationDocumentType,
    isNamesEqual,
    nextStatus,
    status,
    statusesConfig.object,
    value,
    isAceptOrFinal,
  ]);

  return (
    <ConfirmationDialog
      title='Are you sure?'
      maxWidth='md'
      papeprWidth={500}
      description={description}
      titleColor={titleColor}
      descriptionColor={descriptionColor}
      error={changeRiskError || changeStatusError}
      confirmText={actions[nextStatus]?.displayName}
      confirmAction={handleOk}
      disabled={changeStatusLoading || changeRiskLoading}
      onClose={handleCancel}
      isOpen={open}
      confirmButtonColor={statusesConfig.object[nextStatus]?.color}
    >
      {!isAceptOrFinal ? (
        <StatusReasonForm
          reason={mainEntity}
          otherReason={additionalEntity}
          setReason={setMainEntity}
          setOtherReason={setAdditionalEntity}
          validationErrors={validationErrors}
        />
      ) : (
        <RiskLevelForm
          riskLevel={mainEntity}
          riskLevelReason={additionalEntity}
          setRiskLevel={setMainEntity}
          setRiskLevelReason={setAdditionalEntity}
        />
      )}
    </ConfirmationDialog>
  );
};
